// Copyright (C) 2020-2022 Intel Corporation
//
// SPDX-License-Identifier: MIT

import React from 'react';
import Icon, {
    CaretDownOutlined,
    CaretUpFilled,
    EyeInvisibleFilled,
    EyeOutlined,
    LockFilled,
    UnlockOutlined,
} from '@ant-design/icons';
import Select from 'antd/lib/select';

import CVATTooltip from 'components/common/cvat-tooltip';
import { StatesOrdering, Workspace } from 'reducers';
import { ShowGroundTruthIcon } from 'icons';
import { t } from 'utils/i18n';
import SidebarToolbar from './sidebar-toolbar';

interface Props {
    labelSearch: string;
    setLabelSearch: (v: string) => void;
    workspace: Workspace;
    readonly: boolean;
    statesHidden: boolean;
    statesLocked: boolean;
    statesCollapsed: boolean;
    statesOrdering: StatesOrdering;
    switchLockAllShortcut: string;
    switchHiddenAllShortcut: string;
    showGroundTruth: boolean;
    changeStatesOrdering(value: StatesOrdering): void;
    lockAllStates(): void;
    unlockAllStates(): void;
    collapseAllStates(): void;
    expandAllStates(): void;
    hideAllStates(): void;
    showAllStates(): void;
    changeShowGroundTruth(): void;
}

function ObjectListHeader(props: Props): JSX.Element {
    const {
        labelSearch, setLabelSearch, workspace, readonly, statesHidden, statesLocked, statesCollapsed, statesOrdering,
        switchLockAllShortcut, switchHiddenAllShortcut, showGroundTruth, changeStatesOrdering, lockAllStates,
        unlockAllStates, collapseAllStates, expandAllStates, hideAllStates, showAllStates, changeShowGroundTruth,
    } = props;

    return (
        <div className='cvat-objects-sidebar-states-header'>
            <SidebarToolbar search={labelSearch} onSearch={setLabelSearch}>
                {!readonly && (
                    <CVATTooltip title={t('Switch lock property for all {key}', { key: switchLockAllShortcut })}>
                        {statesLocked ? (
                            <LockFilled onClick={unlockAllStates} />
                        ) : (
                            <UnlockOutlined onClick={lockAllStates} />
                        )}
                    </CVATTooltip>
                )}
                <CVATTooltip title={t('Switch hidden property for all {key}', { key: switchHiddenAllShortcut })}>
                    {statesHidden ? (
                        <EyeInvisibleFilled onClick={showAllStates} />
                    ) : (
                        <EyeOutlined onClick={hideAllStates} />
                    )}
                </CVATTooltip>
                {workspace === Workspace.REVIEW_WORKSPACE && (
                    <CVATTooltip title={t('Show Ground truth annotations and conflicts')}>
                        <Icon
                            className={
                                `cvat-objects-sidebar-show-ground-truth ${showGroundTruth ? 'cvat-objects-sidebar-show-ground-truth-active' : ''}`
                            }
                            component={ShowGroundTruthIcon}
                            onClick={changeShowGroundTruth}
                        />
                    </CVATTooltip>
                )}
                <CVATTooltip title={t('Expand/collapse all')}>
                    {statesCollapsed ? (
                        <CaretDownOutlined onClick={expandAllStates} />
                    ) : (
                        <CaretUpFilled onClick={collapseAllStates} />
                    )}
                </CVATTooltip>
                <Select
                    size='small'
                    bordered={false}
                    className='cvat-objects-sidebar-ordering-selector'
                    dropdownClassName='cvat-objects-sidebar-ordering-dropdown'
                    dropdownMatchSelectWidth={false}
                    aria-label={t('Sort by')}
                    value={statesOrdering}
                    onChange={changeStatesOrdering}
                >
                    {Object.values(StatesOrdering).map((ordering) => (
                        <Select.Option key={ordering} value={ordering}>{t(ordering)}</Select.Option>
                    ))}
                </Select>
            </SidebarToolbar>
        </div>
    );
}

export default React.memo(ObjectListHeader);
